<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';
import EvolutionApi from 'dashboard/api/inbox/evolution';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  initialInboxId: { type: [String, Number], default: 0 },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const inboxConfig = ref(null);
const connectionState = ref(null);
const historyItems = ref([]);
const requestId = ref(null);
const pollAttempts = ref(0);
const feedback = ref({ tone: '', message: '' });
const selectedFile = ref(null);
const previewUrl = ref('');
const selectedInboxId = ref('');

const form = ref({
  type: 'text',
  content: '',
  caption: '',
  backgroundColor: '#000000',
  font: '1',
  allContacts: true,
  statusJidList: '',
});

let pollTimer = null;

const currentChat = computed(() => store.getters.getSelectedChat || {});
const availableInboxes = computed(() => {
  return store.getters['inboxes/getInboxes'].filter(inbox => {
    const channelType = inbox.channelType || inbox.channel_type;
    return channelType === INBOX_TYPES.API;
  });
});
const hasMultipleAvailableInboxes = computed(
  () => availableInboxes.value.length > 1
);
const inboxId = computed(() => {
  const parsedInboxId = Number(selectedInboxId.value);

  return Number.isInteger(parsedInboxId) && parsedInboxId > 0
    ? parsedInboxId
    : null;
});
const selectedInbox = computed(() => {
  if (!inboxId.value) {
    return {};
  }

  return store.getters['inboxes/getInbox'](inboxId.value) || {};
});
const hasEvolution = computed(() => Boolean(inboxConfig.value?.has_evolution));
const isImageType = computed(() => form.value.type === 'image');
const isSubmitDisabled = computed(() => {
  if (!inboxId.value || isSubmitting.value) return true;
  if (form.value.type === 'text') return !form.value.content.trim();

  return !selectedFile.value && !form.value.content.trim();
});

const connectionBadgeClass = computed(() => {
  if (!hasEvolution.value) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }

  if (connectionState.value?.connected) {
    return 'bg-n-teal-3 text-n-teal-11';
  }

  return 'bg-n-amber-3 text-n-amber-11';
});

const connectionLabel = computed(() => {
  if (!hasEvolution.value) {
    return t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.NOT_CONFIGURED');
  }

  return connectionState.value?.connected
    ? t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.CONNECTED')
    : t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.DISCONNECTED');
});

function resolveInitialInboxId() {
  const preferredInboxId = Number(
    props.initialInboxId || currentChat.value?.inbox_id
  );

  if (
    Number.isInteger(preferredInboxId) &&
    preferredInboxId > 0 &&
    availableInboxes.value.some(inbox => inbox.id === preferredInboxId)
  ) {
    return String(preferredInboxId);
  }

  if (availableInboxes.value.length === 1) {
    return String(availableInboxes.value[0].id);
  }

  return '';
}

function clearPollTimer() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function revokePreview() {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = '';
  }
}

function resetForm() {
  clearPollTimer();
  requestId.value = null;
  pollAttempts.value = 0;
  feedback.value = { tone: '', message: '' };
  selectedFile.value = null;
  selectedInboxId.value = resolveInitialInboxId();
  inboxConfig.value = null;
  connectionState.value = null;
  historyItems.value = [];
  revokePreview();
  form.value = {
    type: 'text',
    content: '',
    caption: '',
    backgroundColor: '#000000',
    font: '1',
    allContacts: true,
    statusJidList: '',
  };
}

function setFeedback(tone, message) {
  feedback.value = { tone, message };
}

function parseStatusJidList() {
  return form.value.statusJidList
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

function formatHistoryTimestamp(value) {
  if (!value) return '—';

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function historyStateLabel(item) {
  if (item.posted_confirmed) {
    return t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.CONFIRMED');
  }

  if (item.state === 'finished') {
    return t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.SENT');
  }

  if (item.state === 'failed') {
    return t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.FAILED');
  }

  return item.state || '—';
}

async function refreshHistory() {
  if (!inboxId.value) return;

  try {
    const response = await EvolutionApi.getHistory(inboxId.value, 15);
    historyItems.value = response.data?.data || [];
  } catch (error) {
    setFeedback(
      'error',
      error?.response?.data?.error ||
        t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.HISTORY_ERROR')
    );
  }
}

async function loadDialogData() {
  if (!inboxId.value) {
    return;
  }

  isLoading.value = true;
  setFeedback('', '');

  try {
    const [configResponse, historyResponse] = await Promise.all([
      EvolutionApi.getInboxConfig(inboxId.value),
      EvolutionApi.getHistory(inboxId.value, 15),
    ]);

    inboxConfig.value = configResponse.data;
    historyItems.value = historyResponse.data?.data || [];

    try {
      const connectionResponse = await EvolutionApi.checkConnection(
        inboxId.value
      );
      connectionState.value = connectionResponse.data;
    } catch {
      connectionState.value = { connected: false };
    }
  } catch (error) {
    setFeedback(
      'error',
      error?.response?.data?.error ||
        t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
}

function openDialog() {
  resetForm();
  dialogRef.value?.open();

  if (!availableInboxes.value.length) {
    setFeedback(
      'error',
      t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.NO_INBOXES')
    );
    return;
  }

  if (inboxId.value) {
    loadDialogData();
  }
}

function closeDialog() {
  clearPollTimer();
  dialogRef.value?.close();
}

function handleClose() {
  clearPollTimer();
}

function handleInboxChange() {
  clearPollTimer();
  requestId.value = null;
  pollAttempts.value = 0;
  isSubmitting.value = false;
  inboxConfig.value = null;
  connectionState.value = null;
  historyItems.value = [];
  setFeedback('', '');

  if (inboxId.value) {
    loadDialogData();
  }
}

function handleFileChange(event) {
  const [file] = event.target.files || [];
  selectedFile.value = file || null;
  revokePreview();

  if (selectedFile.value) {
    previewUrl.value = URL.createObjectURL(selectedFile.value);
  }
}

function buildPayload() {
  const statusJidList = parseStatusJidList();

  if (form.value.type === 'image' && selectedFile.value) {
    const payload = new FormData();
    payload.append('inbox_id', inboxId.value);
    payload.append('type', form.value.type);
    payload.append('caption', form.value.caption);
    payload.append('allContacts', form.value.allContacts ? 'true' : 'false');

    if (form.value.content.trim()) {
      payload.append('content', form.value.content.trim());
    }

    payload.append('file', selectedFile.value);
    statusJidList.forEach(jid => payload.append('statusJidList[]', jid));
    return payload;
  }

  return {
    inbox_id: inboxId.value,
    type: form.value.type,
    content: form.value.content.trim(),
    caption: form.value.caption.trim(),
    backgroundColor: form.value.backgroundColor,
    font: form.value.font,
    allContacts: form.value.allContacts,
    statusJidList,
  };
}

async function pollRequestStatus() {
  if (!requestId.value) return;

  try {
    pollAttempts.value += 1;
    const response = await EvolutionApi.getRequestStatus(requestId.value);
    const request = response.data?.request;

    if (!request) {
      throw new Error(
        t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.REQUEST_ERROR')
      );
    }

    if (request.state === 'failed') {
      setFeedback(
        'error',
        request.error ||
          t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.FAILED')
      );
      requestId.value = null;
      isSubmitting.value = false;
      await refreshHistory();
      return;
    }

    if (request.posted_confirmed || request.state === 'finished') {
      setFeedback(
        'success',
        request.posted_confirmed
          ? t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.CONFIRMED')
          : t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.SENT')
      );
      useAlert(
        request.posted_confirmed
          ? t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.CONFIRMED')
          : t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.SENT')
      );
      requestId.value = null;
      isSubmitting.value = false;
      await refreshHistory();
      return;
    }

    if (pollAttempts.value >= 40) {
      setFeedback(
        'info',
        t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.QUEUED')
      );
      requestId.value = null;
      isSubmitting.value = false;
      await refreshHistory();
      return;
    }

    pollTimer = setTimeout(pollRequestStatus, 3000);
  } catch (error) {
    setFeedback(
      'error',
      error?.response?.data?.error ||
        error.message ||
        t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.REQUEST_ERROR')
    );
    requestId.value = null;
    isSubmitting.value = false;
  }
}

async function handleConfirm() {
  if (isSubmitDisabled.value) return;

  if (form.value.type === 'text' && !form.value.content.trim()) {
    setFeedback(
      'error',
      t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.TEXT_REQUIRED')
    );
    return;
  }

  if (
    form.value.type === 'image' &&
    !selectedFile.value &&
    !form.value.content.trim()
  ) {
    setFeedback(
      'error',
      t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.IMAGE_REQUIRED')
    );
    return;
  }

  isSubmitting.value = true;
  setFeedback('', '');

  try {
    const response = await EvolutionApi.create(buildPayload());
    requestId.value = response.data?.request_id || null;
    setFeedback(
      'info',
      t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.QUEUED')
    );

    await refreshHistory();

    if (requestId.value) {
      pollAttempts.value = 0;
      clearPollTimer();
      pollTimer = setTimeout(pollRequestStatus, 3000);
      return;
    }

    isSubmitting.value = false;
    useAlert(t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.SENT'));
  } catch (error) {
    isSubmitting.value = false;
    setFeedback(
      'error',
      error?.response?.data?.error ||
        t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.FAILED')
    );
  }
}

onBeforeUnmount(() => {
  clearPollTimer();
  revokePreview();
});

defineExpose({ openDialog, closeDialog });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="$t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.TITLE')"
    :description="
      $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.DESCRIPTION')
    "
    :confirm-button-label="
      $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.SEND')
    "
    :cancel-button-label="$t('CONVERSATION.HEADER.CLOSE')"
    :disable-confirm-button="isSubmitDisabled"
    :is-loading="isSubmitting"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-5">
      <label
        v-if="availableInboxes.length"
        class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
      >
        {{ $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.INBOX') }}
        <select
          v-model="selectedInboxId"
          class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          @change="handleInboxChange"
        >
          <option value="">
            {{
              $t(
                'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.INBOX_PLACEHOLDER'
              )
            }}
          </option>
          <option
            v-for="inboxOption in availableInboxes"
            :key="inboxOption.id"
            :value="String(inboxOption.id)"
          >
            {{ inboxOption.name }}
          </option>
        </select>
      </label>

      <div class="flex flex-wrap gap-3 items-center justify-between">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.INBOX') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ selectedInbox?.name || '—' }}
          </span>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.INSTANCE') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ inboxConfig?.instance_name || '—' }}
          </span>
        </div>
        <span
          class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
          :class="connectionBadgeClass"
        >
          {{ connectionLabel }}
        </span>
      </div>

      <div
        v-if="feedback.message"
        class="rounded-lg px-3 py-2 text-sm"
        :class="{
          'bg-n-ruby-3 text-n-ruby-11': feedback.tone === 'error',
          'bg-n-teal-3 text-n-teal-11': feedback.tone === 'success',
          'bg-n-blue-3 text-n-blue-11': feedback.tone === 'info',
        }"
      >
        {{ feedback.message }}
      </div>

      <div v-if="isLoading" class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION.LOADING_CONVERSATIONS') }}
      </div>

      <template v-else>
        <div class="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <div class="flex flex-col gap-4">
            <template v-if="inboxId">
              <label
                class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
              >
                {{
                  $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.TYPE_LABEL')
                }}
                <select
                  v-model="form.type"
                  class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                >
                  <option value="text">
                    {{
                      $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.TEXT')
                    }}
                  </option>
                  <option value="image">
                    {{
                      $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.IMAGE')
                    }}
                  </option>
                </select>
              </label>

              <label
                class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
              >
                {{
                  isImageType
                    ? $t(
                        'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.IMAGE_HINT'
                      )
                    : $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.CONTENT')
                }}
                <textarea
                  v-model="form.content"
                  rows="4"
                  class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                />
              </label>

              <template v-if="isImageType">
                <label
                  class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
                >
                  {{
                    $t(
                      'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.IMAGE_FILE'
                    )
                  }}
                  <input
                    type="file"
                    accept="image/*"
                    class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                    @change="handleFileChange"
                  />
                </label>

                <div
                  v-if="previewUrl"
                  class="rounded-xl border border-dashed border-n-strong p-3"
                >
                  <img
                    :src="previewUrl"
                    :alt="
                      $t(
                        'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.MEDIA_PREVIEW'
                      )
                    "
                    class="max-h-56 rounded-lg object-contain"
                  />
                </div>

                <label
                  class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
                >
                  {{
                    $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.CAPTION')
                  }}
                  <textarea
                    v-model="form.caption"
                    rows="3"
                    class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                  />
                </label>
              </template>

              <template v-else>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label
                    class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
                  >
                    {{
                      $t(
                        'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.BACKGROUND_COLOR'
                      )
                    }}
                    <input
                      v-model="form.backgroundColor"
                      type="text"
                      class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                    />
                  </label>
                  <label
                    class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
                  >
                    {{
                      $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.FONT')
                    }}
                    <select
                      v-model="form.font"
                      class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                    >
                      <option
                        v-for="font in [1, 2, 3, 4, 5]"
                        :key="font"
                        :value="String(font)"
                      >
                        {{ font }}
                      </option>
                    </select>
                  </label>
                </div>
              </template>

              <label
                class="inline-flex items-center gap-2 text-sm text-n-slate-12"
              >
                <input v-model="form.allContacts" type="checkbox" />
                {{
                  $t(
                    'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.ALL_CONTACTS'
                  )
                }}
              </label>

              <label
                class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
              >
                {{ $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.JIDS') }}
                <textarea
                  v-model="form.statusJidList"
                  rows="3"
                  class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                />
                <span class="text-xs font-normal text-n-slate-11">
                  {{
                    $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.JIDS_HINT')
                  }}
                </span>
              </label>
            </template>

            <div
              v-else-if="hasMultipleAvailableInboxes"
              class="rounded-xl bg-n-alpha-2 px-4 py-6 text-sm text-n-slate-11"
            >
              {{
                $t(
                  'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.INBOX_PLACEHOLDER'
                )
              }}
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <div class="flex items-center justify-between gap-3">
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{
                  $t(
                    'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.HISTORY_TITLE'
                  )
                }}
              </h4>
              <button
                type="button"
                class="rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-12"
                @click="refreshHistory"
              >
                {{ $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.REFRESH') }}
              </button>
            </div>

            <div
              v-if="!historyItems.length"
              class="rounded-xl bg-n-alpha-2 px-4 py-6 text-sm text-n-slate-11"
            >
              {{
                $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.EMPTY_HISTORY')
              }}
            </div>

            <div
              v-else
              class="flex max-h-[28rem] flex-col gap-3 overflow-y-auto pr-1"
            >
              <div
                v-for="item in historyItems"
                :key="item.request_id"
                class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="mb-1 text-sm font-semibold text-n-slate-12">
                      {{
                        item.type ||
                        $t('CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.TEXT')
                      }}
                    </p>
                    <p class="mb-0 text-xs text-n-slate-11">
                      {{ formatHistoryTimestamp(item.created_at) }}
                    </p>
                  </div>
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ historyStateLabel(item) }}
                  </span>
                </div>

                <p
                  class="mb-0 mt-3 whitespace-pre-wrap break-words text-sm text-n-slate-12"
                >
                  {{ item.caption || item.content_preview || '—' }}
                </p>

                <p v-if="item.error" class="mb-0 mt-2 text-xs text-n-ruby-11">
                  {{ item.error }}
                </p>

                <a
                  v-if="item.media_url"
                  :href="item.media_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="mt-3 block"
                >
                  <img
                    :src="item.media_thumbnail_url || item.media_url"
                    :alt="
                      $t(
                        'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.MEDIA_PREVIEW'
                      )
                    "
                    class="max-h-40 rounded-lg border border-n-weak bg-n-alpha-2 object-contain"
                  />
                </a>

                <div
                  v-if="item.media_url || item.media_download_url"
                  class="mt-3 flex flex-wrap gap-3 text-xs"
                >
                  <a
                    v-if="item.media_url"
                    :href="item.media_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-n-brand"
                  >
                    {{
                      $t(
                        'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.MEDIA_OPEN'
                      )
                    }}
                  </a>
                  <a
                    v-if="item.media_download_url"
                    :href="item.media_download_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-n-brand"
                  >
                    {{
                      $t(
                        'CONVERSATION.NATIVE_ACTIONS.EVOLUTION_STATUS.MEDIA_DOWNLOAD'
                      )
                    }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </Dialog>
</template>
